import { useState } from 'react';
import { pictureUrl } from '../api/catalog';
import { SUPPORT_ICONS, supportKeysOf } from './supportIcons';
import { ca } from '../i18n/ca';

/**
 * The picture of one support on the briefing: the game's own picture of the class the night makes
 * it of (the gun, the plane, the helicopter), and the support's Bootstrap icon where there is none.
 *
 * The class comes from the platform (`supports` of the session view) and is only ever a question
 * for the picture service, never text. That service asks the panel's public vehicle image, so when
 * the panel is down, has not extracted the class or the night names none, the request fails (or no
 * class arrives) and the icon stands: the briefing reads the same, only plainer.
 */
export function SupportPicture({
  kind,
  assetClass,
  modSetHash,
  size = 140,
  compact = false,
}: {
  /** The support kind: artillery, cas, transport, supplyDrop, uav, medevac. */
  kind: string;
  /** The class the runtime makes the support of, when the night names one. */
  assetClass?: string | null;
  modSetHash?: string | null;
  size?: number;
  /** A small picture beside a name, as in a pill, and the bare icon where there is none. */
  compact?: boolean;
}) {
  // One state per class: another class is another picture to try.
  const [brokenClass, setBrokenClass] = useState<string | null>(null);
  if (assetClass && brokenClass !== assetClass) {
    return (
      <img
        className={compact ? 'support-thumb' : 'asset-picture thumb'}
        src={pictureUrl('vehicle', assetClass, modSetHash ?? undefined)}
        width={compact ? 48 : size}
        height={compact ? 24 : size}
        alt=""
        aria-hidden="true"
        loading="lazy"
        onError={() => setBrokenClass(assetClass)}
      />
    );
  }
  if (compact) {
    return <i className={`bi bi-${SUPPORT_ICONS[kind] ?? 'life-preserver'}`} aria-hidden="true" />;
  }
  return (
    <span className="support-picture">
      <i className={`bi bi-${SUPPORT_ICONS[kind] ?? 'life-preserver'}`} aria-hidden="true" />
    </span>
  );
}

/** One support the night offers, with the class the platform says it is made of, when it says. */
export type SupportPick = { kind: string; className: string; displayName: string };

/**
 * The supports of a night as tiles: the game's picture of each (or its icon), its name and the
 * name of what it is made of. The briefing and the session page draw the same tiles, so a support
 * the briefing shows is never missing from the session page (the owner, web card 187, on an ended
 * session: «no hi surten els suports»). The keys are the definition's supports with a count; a
 * night with none says so in a line instead of leaving the place blank.
 */
export function SupportTiles({
  supports,
  picks,
  modSetHash,
  empty,
}: {
  /** The definition's `supports`: a key per support, its count of calls. */
  supports: Record<string, number> | undefined;
  /** The session view's `supports`: the class and name each support is made of. */
  picks: SupportPick[] | undefined;
  modSetHash?: string | null;
  /** What a night without supports says. */
  empty: string;
}) {
  const keys = supportKeysOf(supports);
  if (keys.length === 0) return <p className="muted small">{empty}</p>;
  return (
    <ul className="plain start-vehicles support-tiles">
      {keys.map((k) => {
        const pick = picks?.find((p) => p.kind === k);
        return (
          <li key={k}>
            {/* The game's own picture of what it is made of; its class is never text. */}
            <SupportPicture kind={k} assetClass={pick?.className} modSetHash={modSetHash} />
            <strong>{ca.supportNames[k] ?? k}</strong>
            {pick?.displayName && <span className="muted small">{pick.displayName}</span>}
          </li>
        );
      })}
    </ul>
  );
}
